/**
 * hud.ts — on-screen room info: code, roster, and the F3 readout.
 *
 * The event log used to live here too. It moved to `ui/chat.ts`, because to a
 * player "Alice joined" and "Alice: hello" are the same running account of the
 * room and were appearing in two different corners of the screen.
 *
 * The readout makes the snapshot stream visible so "is it interpolating?" is a
 * fact rather than a feeling.
 */

import { FEEL, MAX_PLAYERS, type PlayerInfo } from '@dueler/shared';

export interface DebugReadout {
  tick: number;
  seq: number;
  players: number;
  lagMs: number;
  interpolating: boolean;
  pointerLocked: boolean;
  /** Whether the local player is predicted rather than interpolated. */
  predicting: boolean;
  /** Commands sent but not yet acknowledged — roughly the round trip, in ticks. */
  pendingInputs: number;
  /** Reconciliation error currently being blended out, m. */
  predictErrorM: number;
  /** Whether the camera is unhooked from the body. */
  freeLook: boolean;
}

export interface Hud {
  setRoom(code: string, selfSlot: number): void;
  setRoster(players: PlayerInfo[]): void;
  setDebug(readout: DebugReadout): void;
  setDebugVisible(visible: boolean): void;
  /** Call once per rendered frame with the frame timestamp. */
  tickFrame(now: number): void;
  dispose(): void;
}

export function createHud(): Hud {
  const root = document.createElement('div');
  root.className = 'hud-panel';
  root.innerHTML = `
    <div class="hud-room" id="room">connecting…</div>
    <div class="hud-roster" id="roster"></div>
    <div class="hud-debug" id="debug" hidden></div>
    <div class="hud-fps" id="fps"></div>
  `;
  document.body.appendChild(root);

  const roomEl = root.querySelector<HTMLDivElement>('#room')!;
  const rosterEl = root.querySelector<HTMLDivElement>('#roster')!;
  const debugEl = root.querySelector<HTMLDivElement>('#debug')!;
  const fpsEl = root.querySelector<HTMLDivElement>('#fps')!;

  let selfSlot = -1;

  // Frame timing, averaged over a short window so the number is readable rather
  // than flickering every frame.
  let frames = 0;
  let windowStart = 0;
  let fps = 0;

  return {
    setRoom(code, slot) {
      selfSlot = slot;
      roomEl.textContent = `room ${code}`;
    },
    setRoster(players) {
      rosterEl.textContent = `players ${players.length}/${MAX_PLAYERS}`;
      for (const p of players) {
        const row = document.createElement('div');
        row.className = p.slot === selfSlot ? 'you' : '';
        // textContent, not innerHTML: names are untrusted input.
        row.textContent = p.slot === selfSlot ? `${p.name} (you)` : p.name;
        rosterEl.appendChild(row);
      }
    },
    setDebug(readout) {
      if (debugEl.hidden) return;
      debugEl.textContent =
        `tick ${readout.tick} · seq ${readout.seq} · ${readout.players} entities · ` +
        `lag ${readout.lagMs}ms · ${readout.interpolating ? 'interpolating' : 'holding'} · ` +
        `${readout.pointerLocked ? 'mouse locked' : 'click to lock'} · ${fps} fps · ` +
        `sim ${FEEL.TICK_HZ}Hz · ` +
        (readout.predicting
          ? `predict ${readout.pendingInputs} ahead, err ${(readout.predictErrorM * 100).toFixed(1)}cm`
          : 'predict off') +
        (readout.freeLook ? ' · FREE LOOK' : '');
    },
    setDebugVisible(visible) {
      debugEl.hidden = !visible;
    },
    tickFrame(now) {
      frames += 1;
      if (windowStart === 0) windowStart = now;
      const elapsed = now - windowStart;
      if (elapsed >= 500) {
        fps = Math.round((frames * 1000) / elapsed);
        frames = 0;
        windowStart = now;
        fpsEl.textContent = `${fps} fps`;
      }
    },
    dispose: () => root.remove(),
  };
}
